/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @angular-eslint/component-selector */
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  AddComponent,
  ConfigDownloadDataModel,
  ConfigTableModel,
  CustomerModel,
  DataListModel,
  FiltersModel,
  ListResults,
} from '@kanzi-apes/kanzi-models';
import * as FileSaver from 'file-saver';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { MenuItem, SortEvent } from 'primeng/api';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { EMPTY, expand, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { DynamicTableService } from '../../services/dynamic-table.service';
import { ProgressBarService } from '../../services/progress-bar.service';

/**
 * @author Hugo Andrés Escobar Ciceri
 * @version 5.0.0
 *
 * Component to create a configurable table.
 */
@Component({
    selector: 'kanzi-dynamic-table',
    templateUrl: './kanzi-dynamic-table.component.html',
    styleUrls: ['./kanzi-dynamic-table.component.scss'],
    standalone: false
})
export class KanziDynamicTableComponent
  implements OnInit, OnChanges, OnDestroy
{
  @ViewChild('dt') tableComponent!: Table;
  @Input() dataSource: DataListModel | ListResults<any> | null;
  @Input() hasNextPage = false;
  @Input()
  set colSource(cols: any[]) {
    this.colSourceTransform = cols.map((col) => {
      if ((col.field as string).toLowerCase().includes('status')) {
        return {
          ...col,
          typeData: 'tag',
          pipe: false,
        };
      }

      return col;
    });
  }
  get colSource() {
    return this.colSourceTransform;
  }

  @Input() configs: ConfigTableModel | null;
  @Input() pending: boolean | null;
  @Input() customer: CustomerModel | null;
  @Input() filters: FiltersModel | null;
  @Input() dateFilterOption: string;
  @Input() dynamicComponent: AddComponent | null;
  @Input() isDownloadAll = false;
  @Input() downloadConfigs: ConfigDownloadDataModel | null = null;
  @Input() transformDataFn?: (data: any) => any;
  @Input() searchPlaceholder: string | null = null;
  @Output() paginationEvent = new EventEmitter<FiltersModel>();
  @Output() selectEvent = new EventEmitter<any>();
  @Output() editEvent = new EventEmitter<any>();
  @Output() deleteEvent = new EventEmitter<any>();
  @Output() unsubcribeEvent = new EventEmitter<any>();
  @Output() filterEvent = new EventEmitter<FiltersModel>();
  @Output() clearEvent = new EventEmitter<boolean>();
  @Output() clearDateRangeEvent = new EventEmitter<FiltersModel>();
  @Output() multiselectItemsEvent = new EventEmitter<any[]>();
  @Output() expandRowEvent = new EventEmitter<any>();
  @Output() loadDatasetEvent = new EventEmitter<FiltersModel>();
  @Output() clearAllFiltersColumns = new EventEmitter<boolean>();
  @Output() sortCustomField = new EventEmitter<FiltersModel>();
  @Output() searchCustomFilter = new EventEmitter<FiltersModel>();
  @Output() clickButtonCustom = new EventEmitter<any>();
  @Output() columnFilterEvent = new EventEmitter<Record<string, any[]>>();
  @Output() rowSelect = new EventEmitter<any>();

  rangeDateCalendar: Date[] | null = null;
  colSourceTransform: any[] = [];
  dataListTable: any[] = [];
  emptyMsg = 'Registros no encontrados.';
  showFilter = false;
  selectedItems: any[] = [];
  filteredTotals = 0;
  /**
   * Total records of the initial (unfiltered) query. In lazy mode it is only
   * refreshed while no search/date filter is active, so it stays fixed as the
   * grand total while the user filters (the backend count of a filtered query
   * feeds `filteredTotals` instead).
   */
  totalRecords = 0;
  /**
   * Whether a server-side (lazy) search term is currently active. Used to know
   * if `filteredTotals` should reflect the backend result count or stay at 0.
   */
  private hasActiveSearch = false;

  /**
   * Guard for the synchronous `onLazyLoad` that PrimeNG fires while
   * `dt.reset()` runs inside `clearFilters`. The Refresh action already reloads
   * the data through `clearEvent`, so this flag makes `loadLazy` skip that one
   * reset-triggered emission and avoid a duplicate backend request.
   */
  private isResetting = false;
  exportColumns: any[] = [];
  exportExcelColumns: any[] = [];
  searchFilter: UntypedFormControl;
  items: MenuItem[] = [];
  formFilters!: UntypedFormGroup;
  columSelect: any;
  objAux: any = {};
  colsOptionsFilter: any[] = [];
  downloadData: any[] = [];
  downloadCount = 1;
  isVisibleProgressBar = false;

  /**
   * Delay (in milliseconds) applied to the search input before emitting a
   * request. Prevents firing one backend call per keystroke when the user
   * types quickly.
   */
  private static readonly SEARCH_DEBOUNCE_TIME = 300;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private readonly downloadService: DynamicTableService,
    public readonly progressBarService: ProgressBarService
  ) {
    this.dataSource = null;
    this.colSource = [];
    this.configs = null;
    this.pending = false;
    this.customer = null;
    this.filters = null;
    this.dateFilterOption = '';
    this.dynamicComponent = null;
    this.searchFilter = this.fb.control('');
  }

  ngOnInit() {
    this.items = [
      {
        label: 'Descargar CSV',
        icon: 'pi pi-file-o',
        visible: !this.isDownloadAll,
        command: () => {
          this.tableComponent.exportCSV();
        },
      },
      {
        label: 'Descargar XLS',
        icon: 'pi pi-file-excel',
        command: () => {
          if (this.isDownloadAll && this.downloadConfigs) {
            this.getAllData(
              {
                path: this.downloadConfigs?.path,
                filters: this.downloadConfigs?.filters,
                count: this.downloadConfigs.count,
              },
              'XLS'
            );
          } else {
            if (this.dataSource?.results) {
              this.exportExcel(this.dataSource?.results);
            }
          }
        },
      },
      {
        label: 'Descargar PDF',
        icon: 'pi pi-file-pdf',
        command: () => {
          if (this.isDownloadAll && this.downloadConfigs) {
            this.getAllData(
              {
                path: this.downloadConfigs?.path,
                filters: this.downloadConfigs?.filters,
                count: this.downloadConfigs.count,
              },
              'PDF'
            );
          } else {
            if (this.dataSource?.results) {
              this.exportPdf(this.dataSource?.results);
            }
          }
        },
      },
    ];

    this.searchFilter.valueChanges
      .pipe(
        debounceTime(KanziDynamicTableComponent.SEARCH_DEBOUNCE_TIME),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((query) => {
        if (this.configs?.lazy) {
          const term = (query ?? '').trim();
          this.hasActiveSearch = term.length > 0;
          // With no active search there is nothing filtered: reset the counter
          // immediately so the UI never shows a stale "Total Filtrados".
          if (!this.hasActiveSearch) {
            this.filteredTotals = 0;
          }

          // Reset PrimeNG's pagination so a new search starts on page 1.
          // Otherwise loadLazy keeps the previous `first`/page and the backend
          // receives a page index that no longer exists for the filtered
          // result, returning "Página inválida".
          if (this.tableComponent) {
            this.isResetting = true;
            this.tableComponent.first = 0;
            this.isResetting = false;
          }

          // Send the trimmed term so trailing/leading spaces do not break the
          // backend match.
          this.filters = {
            ...this.filters,
            search: term,
            page: 1,
            first: 0,
          };
          this.searchCustomFilter.emit(this.filters);
        }
      });
  }

  /** @inheritdoc */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getAllData(
    dataDownload: { path: string; filters: any; count: number },
    type: 'PDF' | 'XLS'
  ) {
    this.isVisibleProgressBar = true;
    const totalPacket = Math.ceil(dataDownload.count / 1000);
    this.downloadService
      .getAllData(
        dataDownload.path,
        `${this.downloadCount}/${totalPacket}`,
        dataDownload.filters,
        null
      )
      .pipe(
        expand(({ next }: any) =>
          next
            ? this.downloadService.getAllData(
                dataDownload.path,
                `${this.downloadCount}/${totalPacket}`,
                null,
                next
              )
            : EMPTY
        )
      )
      .subscribe({
        next: (response: unknown) => {
          this.downloadData = this.downloadData.concat(
            (<DataListModel>response).results
          );
          this.downloadCount += 1;
        },
        complete: () => {
          this.isVisibleProgressBar = false;
          this.downloadCount = 1;
          const dataTransform = this.transformDataFn
            ? this.downloadData.map(this.transformDataFn)
            : this.downloadData;
          if (type === 'PDF') {
            this.exportPdf(dataTransform);
          } else if (type === 'XLS') {
            this.exportExcel(dataTransform);
          }
        },
      });
  }

  sortFunction(event: SortEvent) {
    if (!this.configs?.lazy && event) {
      const filtersDate = ['modified', 'created', 'cut_date'];
      event.data?.sort((data1, data2) => {
        let value1: any = data1[event.field!];
        let value2: any = data2[event.field!];
        let result = null;

        if (filtersDate.includes(event.field!)) {
          value1 = new Date(moment(value1, 'DD-MM-YYYY').format('L'));
          value2 = new Date(moment(value2, 'DD-MM-YYYY').format('L'));
        }

        if (value1 == null && value2 != null) result = -1;
        else if (value1 != null && value2 == null) result = 1;
        else if (value1 == null && value2 == null) result = 0;
        else if (typeof value1 === 'string' && typeof value2 === 'string')
          result = value1.localeCompare(value2);
        else result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;

        return event.order! * result;
      });
    }
  }

  /**
   * Handles column header click for lazy-loaded sorting.
   * Toggles between ascending and descending order for the selected column.
   * @param col - The column configuration object containing the orderingField
   */
  customSort(col: any) {
    if (this.configs?.lazy && col['orderingField']) {
      const field = col['orderingField'];
      const currentOrdering = this.filters?.ordering || '';

      if (currentOrdering === field) {
        // Currently ascending → switch to descending
        this.sortCustomField.emit({
          ...this.filters,
          ordering: `-${field}`,
        });
      } else {
        // No ordering, different field, or currently descending → set ascending
        this.sortCustomField.emit({
          ...this.filters,
          ordering: field,
        });
      }
    }
  }

  onSearch(value: any, matchMode: any) {
    if (!this.configs?.lazy) {
      this.tableComponent.filterGlobal(value, matchMode);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource']) {
      if (this.dataSource) {
        this.dataListTable = ([] as any[]).concat(this.dataSource.results);
        this.downloadConfigs = {
          ...this.downloadConfigs,
          count: this.dataSource.count,
        } as ConfigDownloadDataModel;
        // In lazy (server-side) mode the backend count means different things
        // depending on whether a filter is active:
        // - with an active search/date filter it is the number of matches
        //   (Total Filtrados) and the grand total (Total Registros) is kept.
        // - with no filter it is the grand total (Total Registros) and there is
        //   nothing filtered (Total Filtrados = 0).
        if (this.configs?.lazy) {
          const backendCount = this.dataSource.count ?? 0;
          if (this.hasActiveSearch) {
            this.filteredTotals = backendCount;
          } else {
            this.totalRecords = backendCount;
            this.filteredTotals = 0;
          }
        } else if (this.hasActiveSearch) {
          // Report mode (lazy: false) is used by the server-side date-range
          // filter: PrimeNG's native onFilter never fires, so reflect the
          // number of accumulated rows as the "Total Filtrados" count.
          this.filteredTotals = this.dataListTable.length;
        }
      } else {
        this.dataListTable = [];
        if (this.configs?.lazy) {
          this.filteredTotals = 0;
        }
      }
    }
    if (changes['colSource']) {
      if (this.colSource) {
        this.colsOptionsFilter = this.colSource.filter(
          (item) => item.filterOptions && item.filterOptions.length > 0
        );
        const fields = this.colsOptionsFilter.map((item) => item.field);
        fields.forEach((columns) => {
          this.objAux[columns] = [];
        });
        this.formFilters = this.fb.group(this.objAux);
        this.formFilters.valueChanges.subscribe((value) => {
          if (this.columSelect) {
            if (this.configs?.lazy) {
              this.columnFilterEvent.emit(value);
            } else {
              if (this.dataListTable.length > 0 && Array.isArray(this.dataListTable[0][this.columSelect.field])) {
                this.tableComponent.filter(
                  value[this.columSelect.field],
                  this.columSelect.field,
                  'contains'
                );
              } else {
                this.tableComponent.filter(
                  value[this.columSelect.field],
                  this.columSelect.field,
                  'in'
                );
              }
            }
          }
        });
        this.exportColumns = this.colSource.map((col) => ({
          title: col.header,
          dataKey: col.field,
        }));
        this.exportExcelColumns = [];
        this.exportExcelColumns = this.colSource.map((col) => ({
          title: col.header,
          dataKey: col.field,
        }));
        this.selectedItems = [];
      }
    }
  }

  onRowExpand(event: any) {
    this.expandRowEvent.emit(event.data);
  }

  get itemsSelected(): any[] {
    return this.selectedItems;
  }

  set itemsSelected(items: any[]) {
    this.selectedItems = items;
    this.multiselectItemsEvent.emit(items);
  }

  /**
   *
   * @param event {LazyLoadEvent}
   */
  loadLazy(event: TableLazyLoadEvent) {
    // Skip the emission triggered synchronously by dt.reset() during a Refresh:
    // that reload is handled by clearEvent, so emitting here would duplicate
    // the backend request.
    if (this.isResetting) {
      return;
    }
    if (event && event.first !== undefined && event.rows !== undefined) {
      const pag = event.first / event.rows! + 1;
      this.filters = {
        ...this.filters,
        page: pag,
        page_size: this.configs?.rows,
        customer_id: this.customer ? this.customer.id : 0,
        search:
          event.globalFilter === null ? '' : (event.globalFilter as string),
        first: event.first,
      };
      this.paginationEvent.emit(this.filters);
    }
  }

  /**
   *
   * @param el {any}
   *
   * Function to emit select item action with item data.
   */
  onRowSelect(el: any) {
    this.selectEvent.emit(el);
  }

  /**
   *
   * @param el {any}
   *
   * Function to emit select item action with item data.
   */
  onRowSelected(el: any) {
    this.rowSelect.emit(el.data);
  }

  /**
   *
   * @param el {any}
   *
   * Function to emit delete action with the item data.
   */
  onRowEdit(el: any) {
    this.editEvent.emit(el);
  }

  /**
   *
   * @param el {any}
   *
   * Function to emit delete action with the item data.
   */
  onRowDelete(el: any) {
    this.deleteEvent.emit(el);
  }
  onRowUnsubscribe(el: any) {
    this.unsubcribeEvent.emit(el);
  }
  onFilter(filters: any) {
    if (filters) {
      this.filteredTotals = filters.filteredValue.length;
    }
  }

  onClickButtonCustom(el: any) {
    this.clickButtonCustom.emit(el);
  }

  onFilterDateTable(dates: Date[]) {
    if (dates[1]) {
      // A date range is an active (server-side) filter too, so the filtered
      // counter should reflect the backend result count once it arrives.
      this.hasActiveSearch = true;
      const initDate = moment(dates[0]).format('MM/DD/YYYY');
      // The backend treats the "max" date as an exclusive midnight boundary
      // (created < max 00:00), so sending the same day would drop every record
      // created later that day (e.g. 17:06). Add one day to the end date so the
      // whole selected final day is included in the range.
      const endDate = moment(dates[1]).add(1, 'day').format('MM/DD/YYYY');

      // Carry the current search term so a consumer that rebuilds the filter
      // from its own base still keeps the active search alongside the range.
      const currentSearch = this.searchFilter.value ?? '';
      this.filters =
        this.dateFilterOption && this.dateFilterOption === 'modified'
          ? {
              ...this.filters,
              search: currentSearch,
              page: 1,
              page_size: 200,
              modified_min: initDate,
              modified_max: endDate,
            }
          : {
              ...this.filters,
              search: currentSearch,
              page: 1,
              page_size: 200,
              created_min: initDate,
              created_max: endDate,
            };

      this.filterEvent.emit(this.filters);
    }
  }

  clearFilters(check: boolean, dt: any) {
    let checked: boolean = check;
    if (checked) {
      // dt.reset() fires onLazyLoad synchronously; guard it so the reset does
      // not issue its own request. The reload is done via clearEvent below.
      this.isResetting = true;
      dt.reset();
      this.isResetting = false;
      this.tableComponent.filters = {};
      this.columSelect = null;
      this.showFilter = false;
      this.filteredTotals = 0;
      this.hasActiveSearch = false;
      this.rangeDateCalendar = null;
      this.formFilters?.reset();
      this.searchFilter.setValue('');
      // Purge the component's OWN internal filter state too. onFilterDateTable
      // stamps modified_*/created_* and an inflated page_size (200) onto
      // `this.filters`; clearFilters previously only reset the PrimeNG native
      // filters and the search input, leaving those date values in
      // `this.filters`. Because loadLazy rebuilds from `{ ...this.filters }`,
      // the stale date range (and page_size) was re-emitted on the next
      // pagination/reload, re-sending the old range to the backend and
      // returning 0 records. Reset it to a clean base so Refresh truly starts
      // from scratch.
      this.filters = {
        ...this.filters,
        search: '',
        page: 1,
        page_size: this.configs?.rows ?? 50,
        created_min: '',
        created_max: '',
        modified_min: '',
        modified_max: '',
      };
      this.clearEvent.emit(check);
    }
    checked = false;
  }

  /**
   * Clears only the date range filter, keeping the search term intact.
   * Triggered by the calendar's "Clear" button. Unlike the Refresh action,
   * this does NOT reset the whole PrimeNG table nor the search input: it only
   * clears the visible date range and emits a dedicated event carrying the
   * current filters with the date range removed but the active `search`
   * preserved (read from the search FormControl so the input stays visible).
   * @param dt - The PrimeNG table reference, used to clear only its date-range filter state
   */
  clearDateRange(dt: any) {
    this.rangeDateCalendar = null;
    // NOTE: we intentionally do NOT call dt.filter(null, 'created', ...) here.
    // The date range is handled through our own server-side flow, not PrimeNG's
    // native column filter, and dt.filter() would fire a delayed onLazyLoad
    // that races the clearDateRangeEvent below and, being last with switchMap,
    // could win WITHOUT the active search term (showing wrong results after
    // clearing the range). The reload is done exclusively via clearDateRangeEvent.
    const searchTerm = (this.searchFilter.value ?? '').trim();
    // After clearing the date range, only a non-empty search keeps the result
    // "filtered"; otherwise the counter must fall back to 0.
    this.hasActiveSearch = searchTerm.length > 0;
    if (!this.hasActiveSearch) {
      this.filteredTotals = 0;
    }
    // Sync the internal filter state: drop the date range AND the inflated
    // report page_size (200) that `onFilterDateTable` set, so any later
    // server-side search starts again from the default page/size instead of
    // re-sending the stale range or page_size.
    // Clear BOTH date field variants so the internal state stays consistent
    // regardless of dateFilterOption: onFilterDateTable sets created_* or
    // modified_* depending on that option, so clearing must drop both.
    this.filters = {
      ...this.filters,
      search: searchTerm,
      page: 1,
      page_size: this.configs?.rows ?? 50,
      created_min: '',
      created_max: '',
      modified_min: '',
      modified_max: '',
    };
    this.clearDateRangeEvent.emit(this.filters);
  }

  clearAllFilters(dt: any) {
    dt.reset();
    this.tableComponent.filters = {};
    this.columSelect = null;
    this.formFilters?.reset();
    this.clearAllFiltersColumns.emit(true);
    this.filteredTotals = 0;
  }

  showFilters() {
    this.showFilter = this.showFilter ? false : true;
  }

  loadAllDataset() {
    this.filters = {
      ...this.filters,
      page: 1,
      page_size: 200,
      created_min: '',
      created_max: '',
    };

    this.loadDatasetEvent.emit(this.filters);
  }
  exportExcel(downloadData: any[]) {
    import('xlsx').then((xlsx) => {
      const headers = this.exportExcelColumns.map((col) => col.title);

      const rows = downloadData.map((item) => {
        const objAux: Record<string, any> = {};
        this.exportExcelColumns.forEach(
          (column: { title: string; dataKey: string }) => {
            objAux[column.title] = item[column.dataKey] || '';
          }
        );
        return objAux;
      });

      if (rows.length) {
        const worksheet = xlsx.utils.json_to_sheet(rows);

        xlsx.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });

        const workbook = {
          Sheets: { data: worksheet },
          SheetNames: ['data'],
        };

        const excelBuffer: any = xlsx.write(workbook, {
          bookType: 'xlsx',
          type: 'array',
        });

        this.saveAsExcelFile(
          excelBuffer,
          this.configs?.fileName || 'kanzi-report'
        );
      }
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    FileSaver.saveAs(
      data,
      fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION
    );
  }

  exportPdf(downloadData: any[]) {
    import('jspdf').then((jsPDF) => {
      const doc = new jsPDF.default('landscape');
      autoTable(doc, {
        columns: this.exportColumns,
        body: downloadData,
        tableWidth: 'auto',
        pageBreak: 'auto',
        margin: 10,
      });
      doc.save(this.configs ? this.configs.fileName + '.pdf' : 'kanzi-report');
    });
  }

  selectFilter($event: any, col: any, overlaypanel: any) {
    this.columSelect = col;
    console.log('col', col);
    if ($event) {
      overlaypanel.toggle($event);
    }
  }

  getBulkArray(bulkObj: {
    [key: string]: number;
  }): { unit: string; amount: number }[] {
    if (Object.keys(bulkObj)?.length === 0) return [];
    return Object.keys(bulkObj).map((key) => ({
      unit: key,
      amount: bulkObj[key],
    }));
  }
}
